import { useState, useEffect, useRef, useCallback } from 'react';
import logger from '../utils/logger';

/**
 * Custom hook to track which messages are currently visible in the viewport.
 *
 * Uses getBoundingClientRect() for synchronous, deterministic visibility detection
 * following the Netsfere approach from MessageListView.getLastSeenMsgIdx().
 *
 * Implementation:
 * - Scans backward from last message in DOM
 * - Finds last message whose bottom edge is at or above viewport bottom
 * - Updates on scroll (debounced with RAF) and messageGroups changes
 *
 * @param {React.RefObject} scrollContainerRef - Reference to the scroll container element
 * @param {Array} messageGroups - Array of message groups (triggers recalculation when changed)
 * @returns {{
 *   visibleEventIds: Array<string>,
 *   getLastVisibleEventId: () => string|null,
 *   getAllVisibleEventIds: () => Array<string>
 * }} Object containing visible message tracking functions
 *
 * @example
 * const { visibleEventIds, getLastVisibleEventId } = useVisibleMessages(
 *   messagesScrollRef,
 *   messageGroups
 * );
 *
 * // Use with read receipts
 * useReadReceipts(roomId, visibleEventIds, !isEditing, getLastVisibleEventId);
 */
export function useVisibleMessages(scrollContainerRef, messageGroups) {
  const [visibleEventIds, setVisibleEventIds] = useState([]);

  /**
   * Get the last (bottom-most) visible message event ID.
   * This is the primary method that determines visibility.
   */
  const getLastVisibleEventId = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      logger.debug('[useVisibleMessages] No scroll container available');
      return null;
    }

    // Get all message elements
    const messageElements = scrollContainer.querySelectorAll('[data-event-id]');
    if (messageElements.length === 0) {
      logger.debug('[useVisibleMessages] No messages with data-event-id found');
      return null;
    }

    try {
      // Get viewport bottom position (relative to viewport)
      const containerRect = scrollContainer.getBoundingClientRect();
      const viewportBottom = containerRect.bottom;

      logger.debug(`[useVisibleMessages] Scanning ${messageElements.length} messages, viewport bottom: ${viewportBottom}`);

      // Scan backward from last message (Netsfere approach)
      // Find the first message whose bottom is above the viewport bottom
      for (let i = messageElements.length - 1; i >= 0; i--) {
        const element = messageElements[i];
        const rect = element.getBoundingClientRect();
        const messageBottom = rect.bottom;

        // Check if message bottom is visible (at or above viewport bottom)
        // Subtract 1 pixel for edge cases (same as Netsfere)
        if (messageBottom - 1 <= viewportBottom) {
          const eventId = element.dataset.eventId;

          logger.debug(`[useVisibleMessages] Last visible message at index ${i}: ${eventId}, bottom: ${messageBottom}`);

          return eventId;
        }
      }

      // If we get here, even the first message is below viewport (shouldn't happen normally)
      logger.debug('[useVisibleMessages] No messages visible in viewport');
      return null;
    } catch (error) {
      logger.error('[useVisibleMessages] Error getting last visible event ID:', error);
      return null;
    }
  }, [scrollContainerRef]);

  /**
   * Get ALL currently visible message event IDs.
   * Returns all messages from the top of the container to the last visible message.
   */
  const getAllVisibleEventIds = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return [];
    }

    const messageElements = scrollContainer.querySelectorAll('[data-event-id]');
    if (messageElements.length === 0) {
      return [];
    }

    try {
      const containerRect = scrollContainer.getBoundingClientRect();
      const viewportBottom = containerRect.bottom;
      const viewportTop = containerRect.top;

      const visible = [];

      // Collect all messages that are at least partially visible
      for (let i = 0; i < messageElements.length; i++) {
        const element = messageElements[i];
        const rect = element.getBoundingClientRect();
        
        // Message is visible if:
        // - Its bottom is below viewport top (not scrolled past)
        // - Its top is above viewport bottom (not scrolled out of view)
        if (rect.bottom > viewportTop && rect.top < viewportBottom) {
          const eventId = element.dataset.eventId;
          if (eventId) {
            visible.push(eventId);
          }
        }
      }

      logger.debug(`[useVisibleMessages] Found ${visible.length} visible messages`);

      return visible;
    } catch (error) {
      logger.error('[useVisibleMessages] Error getting all visible event IDs:', error);
      return [];
    }
  }, [scrollContainerRef]);

  // Update visible event IDs when messageGroups change or on mount
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    let isMounted = true; // Track mount status to prevent state updates after unmount
    let rafId = null;

    // Helper to only update state if the visible IDs actually changed
    // This prevents unnecessary rerenders and potential infinite loops
    const setVisibleIfChanged = (newVisible) => {
      if (!isMounted) return; // Guard against unmounted component

      setVisibleEventIds(prev => {
        const changed =
          newVisible.length !== prev.length ||
          newVisible.some((id, index) => id !== prev[index]);

        return changed ? newVisible : prev;
      });
    };

    if (!scrollContainer) {
      setVisibleIfChanged([]);
      return;
    }

    const updateVisibleMessages = () => {
      if (!isMounted) return; // Guard against unmounted component
      const visible = getAllVisibleEventIds();
      setVisibleIfChanged(visible);
    };

    // Initial calculation
    updateVisibleMessages();

    // Debounced scroll handler with requestAnimationFrame
    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        if (isMounted) { // Guard RAF callback against unmounted component
          updateVisibleMessages();
        }
        rafId = null;
      });
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      isMounted = false; // Mark as unmounted FIRST to prevent any pending operations
      if (rafId) cancelAnimationFrame(rafId);
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [scrollContainerRef, messageGroups, getAllVisibleEventIds]);

  return {
    visibleEventIds,
    getLastVisibleEventId,
    getAllVisibleEventIds,
  };
}

export default useVisibleMessages;
